import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { 
  Wallet, 
  Shield, 
  Smartphone, 
  Eye,
  Check,
  ChevronRight,
  Menu,
  X,
  ArrowRight,
  Calendar,
  TrendingUp,
  Receipt,
  PiggyBank,
  BarChart3,
  Lock,
  Zap,
  Heart,
  Star,
  Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [billing, setBilling] = useState('monthly'); // 'monthly' | 'annual'
  const [checkingOut, setCheckingOut] = useState(null); // 'platinum' | 'diamond' | null

  // If Stripe Checkout sent them back after cancel, scroll to pricing
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('canceled') === '1') {
      setTimeout(() => {
        const el = document.getElementById('precos');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      window.history.replaceState({}, '', '/');
    }
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const handleSubscribe = async (plan) => {
    // If not logged in, remember intent and send to auth
    if (!isAuthenticated) {
      try {
        sessionStorage.setItem(
          'pendingCheckout',
          JSON.stringify({ plan, period: billing }),
        );
      } catch (_e) {
        // ignore storage errors (private mode)
      }
      navigate('/auth');
      return;
    }
    setCheckingOut(plan);
    try {
      const { data, error } = await supabase.functions.invoke(
        'create-checkout-session',
        { body: { plan, period: billing } },
      );
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error('Checkout URL not returned');
      }
    } catch (err) {
      console.error('Checkout failed:', err);
      setCheckingOut(null);
      alert('Não foi possível iniciar o pagamento. Tente novamente.');
    }
  };

  // Display prices based on billing period
  const prices = {
    platinum: {
      monthly: { amount: 'R$ 14,90', suffix: '/mês' },
      annual: { amount: 'R$ 149,00', suffix: '/ano' },
    },
    diamond: {
      monthly: { amount: 'R$ 29,90', suffix: '/mês' },
      annual: { amount: 'R$ 299,00', suffix: '/ano' },
    },
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] overflow-x-hidden" data-testid="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-md border-b-2 border-slate-200 z-50">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-[#1B2A4A] rounded-xl flex items-center justify-center">
                <Wallet className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="text-xl font-bold text-[#1B2A4A]">Bolso</span>
                <span className="text-sm font-medium text-slate-500 ml-1">Sob Controle</span>
              </div>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-6">
              <button onClick={() => scrollToSection('como-funciona')} className="text-base font-medium text-slate-600 hover:text-[#1B2A4A]">
                Como Funciona
              </button>
              <button onClick={() => scrollToSection('funcionalidades')} className="text-base font-medium text-slate-600 hover:text-[#1B2A4A]">
                Funcionalidades
              </button>
              <Link to="/blog" className="text-base font-medium text-slate-600 hover:text-[#1B2A4A]">
                Blog
              </Link>
              <button
                onClick={() => navigate('/auth')}
                className="text-base font-semibold text-[#1B2A4A] hover:underline"
                data-testid="nav-login-btn"
              >
                Entrar
              </button>
              <Button
                onClick={() => navigate('/auth')}
                className="h-12 px-6 bg-[#047857] hover:bg-[#059669] rounded-xl text-base font-bold"
                data-testid="nav-cta-btn"
              >
                Criar Conta Grátis
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center"
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-200 py-4 px-4 space-y-4">
            <button onClick={() => scrollToSection('como-funciona')} className="block w-full text-left text-lg font-medium text-slate-600 py-2">
              Como Funciona
            </button>
            <button onClick={() => scrollToSection('funcionalidades')} className="block w-full text-left text-lg font-medium text-slate-600 py-2">
              Funcionalidades
            </button>
            <Link to="/blog" onClick={() => setMobileMenuOpen(false)} className="block text-lg font-medium text-slate-600 py-2">
              Blog
            </Link>
            <button
              onClick={() => { navigate('/auth'); setMobileMenuOpen(false); }}
              className="block w-full text-left text-lg font-semibold text-[#1B2A4A] py-2"
            >
              Entrar
            </button>
            <Button
              onClick={() => navigate('/auth')}
              className="w-full h-14 bg-[#047857] hover:bg-[#059669] rounded-xl text-lg font-bold"
            >
              Criar Conta Grátis
            </Button>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-16 md:pb-24 px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left - Text */}
            <div className="space-y-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-[#1B2A4A] leading-tight">
                Seu bolso, <span className="text-[#047857] italic">sob controle</span>. Sem complicação.
              </h1>
              <p className="text-xl md:text-2xl text-slate-600 leading-relaxed">
                A plataforma feita para quem quer controle total das contas do mês — sem planilha, 
                sem calculadora, sem dor de cabeça. <strong>Você coloca os números, o Bolso faz toda a matemática.</strong>
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={() => navigate('/auth')}
                  className="h-14 px-8 bg-[#047857] hover:bg-[#059669] rounded-xl text-lg font-bold shadow-lg"
                  data-testid="hero-cta-btn"
                >
                  Começar Agora — É Grátis
                  <ArrowRight size={20} className="ml-2" />
                </Button>
                <Button
                  onClick={() => scrollToSection('como-funciona')}
                  variant="outline"
                  className="h-14 px-8 border-2 border-[#1B2A4A] text-[#1B2A4A] hover:bg-[#1B2A4A] hover:text-white rounded-xl text-lg font-bold"
                >
                  Veja Como Funciona
                </Button>
              </div>
              <div className="flex items-center gap-2 text-green-700">
                <Check size={20} className="text-green-600" />
                <span className="text-base font-medium">100% gratuito para sempre</span>
              </div>
            </div>

            {/* Right - App Mockup */}
            <div className="relative">
              <div className="bg-white rounded-3xl shadow-2xl border-2 border-slate-200 overflow-hidden">
                {/* Mockup Header */}
                <div className="bg-[#1B2A4A] text-white px-6 py-4 flex items-center justify-between">
                  <span className="text-lg font-bold">Bolso</span>
                  <div className="flex gap-2">
                    <span className="text-sm bg-white/20 px-3 py-1 rounded-full">A+</span>
                    <span className="text-sm bg-white/20 px-3 py-1 rounded-full">A-</span>
                  </div>
                </div>
                {/* Month Selector */}
                <div className="bg-slate-100 px-6 py-4 flex items-center justify-center gap-4">
                  <ChevronRight size={24} className="rotate-180 text-[#1B2A4A]" />
                  <span className="text-xl font-bold text-[#1B2A4A]">Janeiro 2026</span>
                  <ChevronRight size={24} className="text-[#1B2A4A]" />
                </div>
                {/* Summary Cards */}
                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                      <p className="text-sm text-slate-500">Receita Líquida</p>
                      <p className="text-xl font-bold text-[#1B2A4A]">R$ 8.450,00</p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                      <p className="text-sm text-slate-500">Total Despesas</p>
                      <p className="text-xl font-bold text-amber-600">R$ 7.203,00</p>
                    </div>
                  </div>
                  <div className="bg-green-50 rounded-xl p-4 border-2 border-green-300">
                    <p className="text-sm text-green-700 font-medium">Saldo Mensal</p>
                    <p className="text-3xl font-extrabold text-green-700">R$ 1.247,00</p>
                  </div>
                </div>
              </div>
              
              {/* Floating Cards */}
              <div className="absolute -left-4 top-1/3 bg-white rounded-xl shadow-lg border border-slate-200 px-4 py-3 float-animation hidden md:block">
                <div className="flex items-center gap-2">
                  <Check size={18} className="text-green-600" />
                  <span className="text-sm font-medium">Aluguel pago</span>
                </div>
              </div>
              <div className="absolute -right-4 bottom-1/4 bg-white rounded-xl shadow-lg border border-slate-200 px-4 py-3 float-animation-delay hidden md:block">
                <p className="text-sm text-slate-500">Você economizou</p>
                <p className="text-lg font-bold text-green-600">R$ 1.247,00</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="py-8 bg-white border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-12">
            <div className="flex items-center gap-4 justify-center">
              <Lock size={32} className="text-[#1B2A4A]" />
              <div>
                <p className="font-bold text-[#1B2A4A]">Seus dados protegidos</p>
                <p className="text-sm text-slate-500">Criptografia de ponta a ponta</p>
              </div>
            </div>
            <div className="flex items-center gap-4 justify-center">
              <Smartphone size={32} className="text-[#1B2A4A]" />
              <div>
                <p className="font-bold text-[#1B2A4A]">Celular e computador</p>
                <p className="text-sm text-slate-500">Acesse de qualquer lugar</p>
              </div>
            </div>
            <div className="flex items-center gap-4 justify-center">
              <Eye size={32} className="text-[#1B2A4A]" />
              <div>
                <p className="font-bold text-[#1B2A4A]">Letras grandes e acessíveis</p>
                <p className="text-sm text-slate-500">Pensado para todas as idades</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 md:py-24 px-4 md:px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-sm font-bold text-[#047857] uppercase tracking-wider mb-2">Por que Bolso Sob Controle?</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#1B2A4A]">
              Chega de se perder nas contas do mês
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="border-2 border-slate-200 hover:border-[#1B2A4A] transition-colors">
              <CardContent className="pt-6 text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Receipt size={32} className="text-amber-600" />
                </div>
                <h3 className="text-xl font-bold text-[#1B2A4A] mb-2">Caderninho não dá conta</h3>
                <p className="text-base text-slate-600">
                  Anotar no papel ou numa planilha complicada? Sempre falta alguma conta, 
                  o cálculo sai errado, e no fim do mês você não sabe pra onde foi o dinheiro.
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 border-slate-200 hover:border-[#1B2A4A] transition-colors">
              <CardContent className="pt-6 text-center">
                <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Zap size={32} className="text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-[#1B2A4A] mb-2">Muita conta, pouca organização</h3>
                <p className="text-base text-slate-600">
                  Aluguel, condomínio, energia, cartão, plano de saúde, dízimo, IPTU... 
                  São dezenas de contas todo mês. Sem organização, o estresse é garantido.
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 border-slate-200 hover:border-[#1B2A4A] transition-colors">
              <CardContent className="pt-6 text-center">
                <div className="w-16 h-16 bg-slate-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <TrendingUp size={32} className="text-slate-600" />
                </div>
                <h3 className="text-xl font-bold text-[#1B2A4A] mb-2">Não saber se sobrou ou faltou</h3>
                <p className="text-base text-slate-600">
                  O mês acaba e você não tem certeza se o saldo ficou positivo ou negativo. 
                  Sem controle, não dá pra planejar nem economizar.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="funcionalidades" className="py-16 md:py-24 px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-sm font-bold text-[#047857] uppercase tracking-wider mb-2">Funcionalidades</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#1B2A4A] mb-4">
              Tudo que você precisa, nada que você não precisa
            </h2>
            <p className="text-xl text-slate-600">O Bolso faz toda a matemática. Você só coloca os números.</p>
          </div>

          <div className="space-y-16">
            {/* Feature 1 */}
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="space-y-4">
                <div className="w-14 h-14 bg-[#1B2A4A] rounded-2xl flex items-center justify-center">
                  <Calendar size={28} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[#1B2A4A]">Navegue entre os meses com um toque</h3>
                <p className="text-lg text-slate-600">
                  Cada mês tem sua própria página com receitas, despesas e saldo. 
                  Navegue com setas grandes ou toque no mês desejado. 
                  Copie os dados do mês anterior para não precisar digitar tudo de novo.
                </p>
                <ul className="space-y-2">
                  {['Seletor de mês sempre visível', 'Botão copiar mês anterior', 'Histórico completo'].map((item) => (
                    <li key={item} className="flex items-center gap-2 text-base">
                      <Check size={18} className="text-green-600" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-slate-100 rounded-2xl p-4 md:p-6 border-2 border-slate-200">
                <div className="flex items-center justify-center gap-2 md:gap-6">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-[#1B2A4A] rounded-xl flex items-center justify-center text-white shrink-0">
                    <ChevronRight size={20} className="rotate-180" />
                  </div>
                  <div className="flex gap-1 md:gap-2 min-w-0">
                    {['Jan', 'Fev', 'Mar', 'Abr', 'Mai'].map((m, i) => (
                      <span
                        key={m}
                        className={`px-2 md:px-4 py-2 rounded-lg text-sm md:text-base font-semibold ${i === 2 ? 'bg-[#1B2A4A] text-white' : 'bg-white text-slate-600'} ${i === 0 || i === 4 ? 'hidden sm:inline-block' : ''}`}
                      >
                        {m}
                      </span>
                    ))}
                  </div>
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-[#1B2A4A] rounded-xl flex items-center justify-center text-white shrink-0">
                    <ChevronRight size={20} />
                  </div>
                </div>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="order-2 md:order-1 bg-slate-100 rounded-2xl p-6 border-2 border-slate-200 space-y-3">
                <div className="bg-white rounded-xl p-4">
                  <p className="text-sm text-slate-500">Receita Bruta</p>
                  <p className="text-xl font-bold text-[#1B2A4A]">R$ 12.000,00</p>
                </div>
                <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                  <p className="text-sm text-red-600">Total Descontos (IRRF, INSS, etc)</p>
                  <p className="text-xl font-bold text-red-600">- R$ 3.550,00</p>
                </div>
                <div className="bg-green-50 rounded-xl p-4 border-2 border-green-300">
                  <p className="text-sm text-green-700">Receita Líquida</p>
                  <p className="text-2xl font-bold text-green-700">R$ 8.450,00</p>
                </div>
              </div>
              <div className="order-1 md:order-2 space-y-4">
                <div className="w-14 h-14 bg-green-600 rounded-2xl flex items-center justify-center">
                  <TrendingUp size={28} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[#1B2A4A]">Vencimento bruto, descontos e líquido — tudo automático</h3>
                <p className="text-lg text-slate-600">
                  Cadastre seu salário ou aposentadoria, aluguéis, 13º, e o Bolso calcula automaticamente 
                  os descontos (IRRF, INSS, plano de saúde, empréstimo consignado) e mostra seu Total Líquido.
                </p>
                <ul className="space-y-2">
                  {['Bruto e líquido calculados automaticamente', 'Todos os descontos organizados', 'Múltiplas fontes de renda'].map((item) => (
                    <li key={item} className="flex items-center gap-2 text-base">
                      <Check size={18} className="text-green-600" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="space-y-4">
                <div className="w-14 h-14 bg-amber-500 rounded-2xl flex items-center justify-center">
                  <Receipt size={28} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[#1B2A4A]">Cada conta no seu lugar — fixa ou eventual</h3>
                <p className="text-lg text-slate-600">
                  Separe suas despesas fixas (aluguel, energia, cartão) das esporádicas (faxineira, presentes). 
                  Marque o que já foi pago e acompanhe quantas contas faltam.
                </p>
                <ul className="space-y-2">
                  {['Categorias pré-cadastradas', 'Marque Pago ou Pendente', 'Subtotais automáticos'].map((item) => (
                    <li key={item} className="flex items-center gap-2 text-base">
                      <Check size={18} className="text-green-600" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-slate-100 rounded-2xl p-6 border-2 border-slate-200 space-y-3">
                {[
                  { name: 'Aluguel', value: 'R$ 2.500,00', paid: true },
                  { name: 'Energia', value: 'R$ 280,00', paid: true },
                  { name: 'Cartão de Crédito', value: 'R$ 1.850,00', paid: false },
                ].map((item) => (
                  <div key={item.name} className={`flex items-center justify-between p-4 rounded-xl ${item.paid ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${item.paid ? 'bg-green-600' : 'bg-amber-300'}`}>
                        {item.paid && <Check size={18} className="text-white" />}
                      </div>
                      <span className="font-medium">{item.name}</span>
                    </div>
                    <span className={`font-bold ${item.paid ? 'text-green-700' : 'text-amber-700'}`}>{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="como-funciona" className="py-16 md:py-24 px-4 md:px-6 bg-[#1B2A4A]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
              Comece em 3 minutos
            </h2>
            <p className="text-xl text-slate-300">Sem complicação. Sem conhecimento técnico. Só precisa saber digitar.</p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: '1', title: 'Crie sua conta', desc: 'Informe seu nome e email. Pronto, já pode começar.', icon: Shield },
              { step: '2', title: 'Cadastre sua renda', desc: 'Coloque seu salário, aposentadoria ou aluguel.', icon: Wallet },
              { step: '3', title: 'Adicione despesas', desc: 'Use as categorias prontas ou crie as suas.', icon: Receipt },
              { step: '4', title: 'Acompanhe o saldo', desc: 'Veja quanto sobrou e defina metas de economia.', icon: PiggyBank },
            ].map((item) => (
              <Card key={item.step} className="bg-white border-2 border-white text-[#1B2A4A]">
                <CardContent className="pt-6 text-center">
                  <div className="w-12 h-12 bg-[#047857] rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold text-white">
                    {item.step}
                  </div>
                  <item.icon size={32} className="mx-auto mb-3 text-[#1B2A4A]" />
                  <h3 className="text-xl font-bold mb-2 text-[#1B2A4A]">{item.title}</h3>
                  <p className="text-base text-slate-600">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Accessibility Section */}
      <section className="py-16 md:py-24 px-4 md:px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl md:text-4xl font-extrabold text-[#1B2A4A]">
                Pensado para ser fácil de usar
              </h2>
              <p className="text-xl text-slate-600">
                Letras grandes, botões claros, cores de alto contraste. 
                Feito para quem quer simplicidade.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Eye, title: 'Tamanho ajustável', desc: 'Botões A+ e A- para aumentar o texto' },
                  { icon: Smartphone, title: 'Botões grandes', desc: 'Tudo é grande o suficiente para tocar' },
                  { icon: Heart, title: 'Alto contraste', desc: 'Cores fortes e claras' },
                  { icon: Lock, title: 'Dados seguros', desc: 'Suas informações protegidas' },
                ].map((item) => (
                  <div key={item.title} className="bg-white rounded-xl p-4 border-2 border-slate-200">
                    <item.icon size={24} className="text-[#1B2A4A] mb-2" />
                    <p className="font-bold text-[#1B2A4A]">{item.title}</p>
                    <p className="text-sm text-slate-500">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-3xl shadow-xl border-2 border-slate-200 p-6">
              <div className="flex justify-between items-center mb-4">
                <span className="text-xl font-bold text-[#1B2A4A]">Exemplo de visualização</span>
                <div className="flex gap-2">
                  <span className="px-3 py-1 bg-slate-100 rounded-lg text-sm font-bold">A+</span>
                  <span className="px-3 py-1 bg-slate-100 rounded-lg text-sm font-bold">A-</span>
                </div>
              </div>
              <div className="space-y-4">
                <div className="h-16 bg-green-50 rounded-xl border-2 border-green-300 flex items-center justify-between px-4">
                  <span className="text-lg font-bold text-green-700">Saldo Mensal</span>
                  <span className="text-2xl font-bold text-green-700">R$ 1.247,00</span>
                </div>
                <div className="h-14 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between px-4">
                  <span className="font-medium">Aluguel</span>
                  <span className="font-bold">R$ 2.500,00</span>
                </div>
                <div className="h-14 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between px-4">
                  <span className="font-medium">Energia</span>
                  <span className="font-bold">R$ 280,00</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="precos" className="py-16 md:py-24 px-4 md:px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-sm font-bold text-[#047857] uppercase tracking-wider mb-2">Planos</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#1B2A4A] mb-4">
              Escolha o plano ideal para você
            </h2>
            <p className="text-xl text-slate-600">
              Comece grátis. Atualize quando precisar.
            </p>
          </div>

          {/* Monthly / Annual toggle */}
          <div className="flex justify-center mb-10">
            <div className="inline-flex bg-white border-2 border-slate-200 rounded-xl p-1 shadow-sm">
              <button
                type="button"
                onClick={() => setBilling('monthly')}
                className={`px-5 py-2 rounded-lg text-base font-semibold transition-colors ${
                  billing === 'monthly'
                    ? 'bg-[#1B2A4A] text-white'
                    : 'text-slate-600 hover:text-[#1B2A4A]'
                }`}
                data-testid="billing-monthly-btn"
              >
                Mensal
              </button>
              <button
                type="button"
                onClick={() => setBilling('annual')}
                className={`px-5 py-2 rounded-lg text-base font-semibold transition-colors flex items-center gap-2 ${
                  billing === 'annual'
                    ? 'bg-[#1B2A4A] text-white'
                    : 'text-slate-600 hover:text-[#1B2A4A]'
                }`}
                data-testid="billing-annual-btn"
              >
                Anual
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                  billing === 'annual' ? 'bg-[#047857] text-white' : 'bg-green-100 text-[#047857]'
                }`}>
                  2 meses grátis
                </span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-stretch">
            {/* Gratuito */}
            <Card className="border-2 border-slate-200 shadow-lg flex flex-col">
              <CardContent className="pt-8 pb-8 flex flex-col flex-1">
                <h3 className="text-2xl font-bold text-[#1B2A4A] mb-2">Gratuito</h3>
                <div className="mb-4">
                  <span className="text-4xl font-extrabold text-[#1B2A4A]">R$ 0</span>
                  <span className="text-base text-slate-500 ml-2">— para sempre</span>
                </div>
                <p className="text-base text-slate-600 mb-6 min-h-[3rem]">
                  Tudo que você precisa para começar a organizar suas contas.
                </p>
                <ul className="space-y-3 mb-8 flex-1">
                  {[
                    '3 fontes de renda',
                    'Despesas ilimitadas',
                    '2 metas de economia',
                    '3 meses de histórico',
                    'Letras ajustáveis',
                    'Suporte em português',
                  ].map((feat) => (
                    <li key={feat} className="flex items-start gap-3 text-base text-slate-700">
                      <Check size={20} className="text-green-600 shrink-0 mt-0.5" />
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => navigate('/auth')}
                  variant="outline"
                  className="w-full h-14 text-lg font-bold border-2 border-[#1B2A4A] text-[#1B2A4A] hover:bg-[#1B2A4A] hover:text-white rounded-xl"
                  data-testid="pricing-gratuito-btn"
                >
                  Começar Grátis
                </Button>
              </CardContent>
            </Card>

            {/* Platinum — highlighted */}
            <Card className="border-4 border-[#047857] shadow-2xl md:scale-105 relative flex flex-col bg-white">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#047857] text-white text-sm font-bold px-4 py-1.5 rounded-full flex items-center gap-1 shadow-md">
                <Star size={14} className="fill-white" />
                Mais Popular
              </div>
              <CardContent className="pt-10 pb-8 flex flex-col flex-1">
                <h3 className="text-2xl font-bold text-[#047857] mb-2">Platinum</h3>
                <div className="mb-4">
                  <span className="text-4xl font-extrabold text-[#1B2A4A]">{prices.platinum[billing].amount}</span>
                  <span className="text-base text-slate-500 ml-2">{prices.platinum[billing].suffix}</span>
                </div>
                <p className="text-base text-slate-600 mb-6 min-h-[3rem]">
                  Controle total, histórico completo, sem limites.
                </p>
                <ul className="space-y-3 mb-8 flex-1">
                  {[
                    'Tudo do plano Gratuito',
                    'Rendas ilimitadas',
                    'Metas ilimitadas',
                    'Histórico completo',
                    'Relatório anual',
                    'Categorias personalizadas',
                    'Alertas inteligentes',
                  ].map((feat) => (
                    <li key={feat} className="flex items-start gap-3 text-base text-slate-700">
                      <Check size={20} className="text-green-600 shrink-0 mt-0.5" />
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => handleSubscribe('platinum')}
                  disabled={checkingOut === 'platinum'}
                  className="w-full h-14 text-lg font-bold bg-[#047857] hover:bg-[#059669] rounded-xl shadow-md"
                  data-testid="pricing-platinum-btn"
                >
                  {checkingOut === 'platinum' ? 'Redirecionando...' : 'Escolher Platinum'}
                </Button>
              </CardContent>
            </Card>

            {/* Diamond */}
            <Card className="border-2 border-slate-200 shadow-lg flex flex-col">
              <CardContent className="pt-8 pb-8 flex flex-col flex-1">
                <h3 className="text-2xl font-bold text-[#1B2A4A] mb-2">Diamond</h3>
                <div className="mb-4">
                  <span className="text-4xl font-extrabold text-[#1B2A4A]">{prices.diamond[billing].amount}</span>
                  <span className="text-base text-slate-500 ml-2">{prices.diamond[billing].suffix}</span>
                </div>
                <p className="text-base text-slate-600 mb-6 min-h-[3rem]">
                  Para quem quer o máximo de organização e praticidade.
                </p>
                <ul className="space-y-3 mb-8 flex-1">
                  {[
                    { label: 'Tudo do Platinum', soon: false },
                    { label: 'Exportar relatório em PDF', soon: false },
                    { label: 'Projeção de 12 meses', soon: false },
                    { label: 'Exportar e importar dados', soon: false },
                    { label: 'Upload de extrato bancário', soon: true },
                    { label: 'Suporte prioritário', soon: false },
                  ].map((feat) => (
                    <li
                      key={feat.label}
                      className={`flex items-start gap-3 text-base ${feat.soon ? 'text-slate-400' : 'text-slate-700'}`}
                    >
                      <Check size={20} className={`${feat.soon ? 'text-slate-300' : 'text-green-600'} shrink-0 mt-0.5`} />
                      <span className="flex items-center gap-2 flex-wrap">
                        {feat.label}
                        {feat.soon && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">
                            <Clock size={12} />
                            Em breve
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => handleSubscribe('diamond')}
                  disabled={checkingOut === 'diamond'}
                  variant="outline"
                  className="w-full h-14 text-lg font-bold border-2 border-[#1B2A4A] text-[#1B2A4A] hover:bg-[#1B2A4A] hover:text-white rounded-xl"
                  data-testid="pricing-diamond-btn"
                >
                  {checkingOut === 'diamond' ? 'Redirecionando...' : 'Escolher Diamond'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 px-4 md:px-6 bg-gradient-to-br from-[#047857] to-[#0D4F4F]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
            Coloque seu bolso sob controle hoje
          </h2>
          <p className="text-xl text-white/80 mb-8">
            É grátis, é simples, e foi feito pensando em você.
          </p>
          <Button
            onClick={() => navigate('/auth')}
            className="h-16 px-12 bg-white text-[#1B2A4A] hover:bg-slate-100 rounded-xl text-xl font-bold shadow-lg"
            data-testid="final-cta-btn"
          >
            Criar Minha Conta Grátis
            <ArrowRight size={24} className="ml-2" />
          </Button>
          <p className="text-white/60 mt-4">Sem cartão de crédito. Sem compromisso.</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 md:px-6 bg-[#1B2A4A]">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center">
                <Wallet className="w-5 h-5 text-[#1B2A4A]" />
              </div>
              <div>
                <span className="text-xl font-bold text-white">Bolso</span>
                <span className="text-sm font-medium text-slate-400 ml-1">Sob Controle</span>
              </div>
            </div>
            <div className="flex flex-col items-center md:items-end gap-2">
              <a
                href="https://www.instagram.com/bolsosobcontrole.com.br"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                  <circle cx="12" cy="12" r="4"/>
                  <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/>
                </svg>
                <span className="text-sm">@bolsosobcontrole.com.br</span>
              </a>
              <p className="text-slate-400 text-sm">
                © 2026 Bolso Sob Controle. Todos os direitos reservados.
              </p>
            </div>
          </div>
          <p className="text-slate-500 text-xs text-center mt-6">
            Bolso Sob Controle pertence a OttoSwitch LLC.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
